import { useIntl } from 'react-intl';
import { useKeycloak } from '@react-keycloak/web';
import {
  ODSGlobalHeaderFlyoutMenu,
  ODSGlobalHeaderFlyoutMenuItem,
  ODSGlobalHeaderFlyoutMenuSub,
  ODSGlobalHeaderFlyoutMenuSubTrigger,
  ODSGlobalHeaderMainLink,
  ODSGlobalHeaderMainMenu,
} from '@telekom-ods/react-ui-kit';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import rbacCheck from '../../rbac/rbacCheck';

const MainNavigation = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak.authenticated;
  const { organization, roles } = useSelector((state) => state.user);

  const permissions = {
    canSeeProducts: rbacCheck(roles, '/products', organization) || !isAuthenticated,
    canSeeOrganizations: rbacCheck(roles, '/organizations:VIEW_ALL'),
    canSeeOwnOrganization: rbacCheck(roles, '/organizations:VIEW_OWN_DETAILS'),
    canOrderCompute: rbacCheck(roles, '/products/compute/order', organization),
    canUseSafescarfInstance: rbacCheck(roles, '/products/safescarf', organization),
    canSeeReports: rbacCheck(roles, '/reports'),
    canSeeMonitoringDashboards: rbacCheck(roles, '/monitoring', organization),
  };

  const item = (labelId, path) => (
    <ODSGlobalHeaderFlyoutMenuItem
      key={labelId}
      label={intl.formatMessage({ id: labelId })}
      onSelect={() => navigate(path)}
    />
  );

  const subMenu = (labelId, children = []) => {
    if (!children || children.length === 0) {
      return <ODSGlobalHeaderFlyoutMenuItem label={intl.formatMessage({ id: labelId })} />;
    }

    const validChildren = children.filter(Boolean);
    return (
      <ODSGlobalHeaderFlyoutMenuSub
        key={labelId}
        trigger={
          <ODSGlobalHeaderFlyoutMenuSubTrigger label={intl.formatMessage({ id: labelId })} />
        }
      >
        {validChildren}
      </ODSGlobalHeaderFlyoutMenuSub>
    );
  };

  return (
    <ODSGlobalHeaderMainMenu>
      {permissions.canSeeProducts && (
        <ODSGlobalHeaderFlyoutMenu
          trigger={<ODSGlobalHeaderMainLink label={intl.formatMessage({ id: 'products' })} />}
        >
          {subMenu('compute', [
            isAuthenticated && item('myProducts', '/products/compute'),
            item('productSpecification', '/products/compute/specification'),
            permissions.canOrderCompute && item('order', '/products/compute/order'),
            isAuthenticated && item('freeTrial', '/products/compute/free-trial'),
          ])}
          {subMenu('safescarf', [
            isAuthenticated && permissions.canUseSafescarfInstance && item('myInstance', '/products/safescarf'),
            item('productSpecification', '/products/safescarf/specification'),
          ])}
          {subMenu('caas', [
            isAuthenticated && item('myClusters', '/products/caas/clusters'),
            isAuthenticated && item('myProducts', '/products/caas-on-openstack'),
            item('productSpecification', '/products/caas/specification'),
          ])}
          {subMenu('monitoring', [
            isAuthenticated && permissions.canSeeMonitoringDashboards && item('myDashboards', '/monitoring-dashboards'),
            item('productSpecification', '/products/monitoring/specification'),
          ])}
        </ODSGlobalHeaderFlyoutMenu>
      )}
      {permissions.canSeeOrganizations && (
        <ODSGlobalHeaderMainLink
          label={intl.formatMessage({ id: 'organizations' })}
          onClick={() => navigate('/organizations')}
        />
      )}
      {permissions.canSeeOwnOrganization && (
        <ODSGlobalHeaderMainLink
          label={intl.formatMessage({ id: 'organization' })}
          onClick={() => navigate(`/organizations/${organization?._id}`)}
        />
      )}
      {permissions.canSeeReports && (
        <ODSGlobalHeaderMainLink
          label={intl.formatMessage({ id: 'reports' })}
          onClick={() => navigate('/reports/tenants')}
        />
      )}
    </ODSGlobalHeaderMainMenu>
  );
};

export default MainNavigation;
